import { useCallback, useMemo, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Credential, Provider } from '../Types';
import useGlobals from './useGlobals';
import { useParams } from 'react-router-dom';
import { credentialsService } from '../Services/credentialsService';
import { useSelector } from 'react-redux';
import { RootState } from '../Store/Reducers';
import { SaveCredential } from '../Types/ServiceTypes';
import { providerService } from '../Services/providerService';

const useCredential = () => {
    const { handleNavigate, userSettings, i18n, demo } = useGlobals();
    let { credentialIdentifier } = useParams();
    const [name, setName] = useState<string>('');
    const [parameters, setParameters] = useState<Record<string, any>>({});
    const [credentialLoaded, setCredentialLoaded] = useState<boolean>(false);
    const [credentialSnapshot, setCredentialSnapshot] = useState<Credential | null>(null);
    const basePrompt = useSelector((state: RootState) => state.environment.basePrompt!);
    const [providers, setProviders] = useState<Provider[]>([]);
    const [providerIdentifier, setProviderIdentifier] = useState<string>('');
    const handleNameChange = useCallback((value: string) => {
        setName(value);
    }, []);

    const handleParameterChange = useCallback((key: string, value: any) => {
        setParameters((prev) => ({ ...prev, [key]: value }));
    }, []);

    const notifySuccess = useCallback(() => {
        toast.success(i18n.translate({ id: 'credentialSaved', sourceName: 'Credential' }));
    }, [i18n]);

    const handleSave = useCallback(
        (name: string) => {
            if (demo) {
                notifySuccess();
                return;
            }

            if (!userSettings.isLoggedIn) return;

            const data = new URLSearchParams() as SaveCredential;
            data.append('name', name);
            data.append('provider', providerIdentifier);
            let error = false;
            const provider = providers.find((provider) => provider.identifier === providerIdentifier);
            Object.entries(provider?.options || {}).forEach(([key, option]) => {
                if (option.required && !parameters[key]) {
                    toast.error(i18n.translate({ id: 'credentialSave.error.required', sourceName: 'Credential' }));
                    error = true;
                }
                if (option.type === 'string') {
                    data.append(`parameters[${key}]`, parameters[key] || '');
                }
            });

            if (error) return;

            let type = 'update/' + credentialIdentifier;
            if (credentialIdentifier === 'new') {
                type = 'create';
            }
            credentialsService.saveCredential(type, data).then((response) => {
                notifySuccess();
                credentialIdentifier === 'new' && handleNavigate('/admin/credentials/' + response.data.identifier);
                setCredentialSnapshot(response.data);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        },
        [
            demo,
            userSettings.isLoggedIn,
            handleNavigate,
            notifySuccess,
            parameters,
            providerIdentifier,
            credentialIdentifier,
            providers,
            i18n,
        ],
    );

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;

        providerService.getProviders().then((providerResponse: { data: Provider[] }) => {
            setProviders(providerResponse.data);

            if (credentialIdentifier !== 'new' && credentialIdentifier !== undefined) {
                credentialsService.getCredential(demo, credentialIdentifier).then((response: { data: Credential }) => {
                    setName(response.data.name);
                    setParameters(response.data.parameters);
                    setCredentialSnapshot(response.data);
                    setCredentialLoaded(true);
                    setProviderIdentifier(response.data.provider);
                });
            }
            if (credentialIdentifier === 'new') {
                setCredentialLoaded(true);
            }
        });
    }, [userSettings.isLoggedIn, credentialIdentifier, demo, basePrompt]);

    const CredentialActions = useMemo(
        () => ({
            handleSave,
            handleNameChange,
            handleParameterChange,
            setProviderIdentifier,
        }),
        [handleSave, handleNameChange, setProviderIdentifier],
    );

    return {
        CredentialActions,
        name,
        parameters,
        credentialIdentifier,
        providers,
        providerIdentifier,
        credentialLoaded,
    };
};

export default useCredential;
